import moment from 'moment';
import { useMemo } from 'react';

import {
  geInboxEntryAllowClose,
  geInboxEntryPermissions,
  mapInboxEntryWorkstreams,
} from '@/src/components/project/Inbox2/models/InboxEntry';
import {
  EmailEntry,
  EmailEntryPermission,
  EmailFileEntry,
  InboxEntryStatus,
} from '@/src/components/project/Inbox2/types';
import { DEFAULT_DATE_TIME_A_FORMAT } from '@/src/config';

import { useEmailEntry } from '../../../../api/getEmailEntry';

type useMappedEmailEntryProps = {
  emailId: number;
  inboxEntryId: number;
  projectId: string;
};

const getEmailEntryPermissions = (rawPermissions: string[]): EmailEntryPermission => {
  const permissions = geInboxEntryPermissions(rawPermissions);

  return {
    trash: permissions['trash'] || false,
    deletePermanent: permissions['permanent_delete'] || false,
    misc: permissions['misc'] || false,
    tagWorkstream: permissions['tag_wokstream'] || false,
    restore: permissions['restore'] || false,
    changeStatus: permissions['change-status'] || false,
    createTask: permissions['create_task'] || false,
    read: permissions['read'] || false,
    print: permissions['print'] || false,
    copyEmailUrl: permissions['copy_url'] || false,
    info: permissions['info'] || false,
    createReply: permissions['reply'] || false,
  };
};

export const mapEmailEntry = ({ emailId, inboxEntryId, rawResponse }): EmailEntry => {
  // filter actual attachments from inline images
  const emailAttachments = rawResponse.attachments.filter((file) => !file.cid);
  return {
    id: emailId,
    from: rawResponse.from.map((from) => ({
      emailAddress: from.address,
      fullName: from.name,
      avatar: from.avatar,
    })),
    to: rawResponse.to.map((to) => ({
      emailAddress: to.address,
      fullName: to.name,
      avatar: to.avatar,
    })),
    cc: rawResponse.cc
      ? rawResponse.cc.map((cc) => ({
          emailAddress: cc.address,
          fullName: cc.name,
          avatar: cc.avatar,
        }))
      : null,
    files: {
      list: emailAttachments.map(
        (file): EmailFileEntry => ({
          emailId,
          date: moment().format(DEFAULT_DATE_TIME_A_FORMAT), // @TODO
          file: {
            id: file.id,
            name: file.name,
            status: file.status,
            isRead: file.read, // @TODO
            user: {
              fullName: 'firstName',
            },
            allowedStatusChanges: file['allowed-status-changes'],
            allowClose: geInboxEntryAllowClose(file['allow-close']),
            duplicate: file.duplicate,
          },
        })
      ),
      total: emailAttachments.length,
    },
    status: rawResponse.status,
    emailType: rawResponse.type,
    source: rawResponse.source,
    dateAdded: rawResponse.received,
    openedDate: rawResponse.opened?.date,
    openedBy: rawResponse.opened?.name,
    closedDate: rawResponse.closed?.date,
    closedBy: rawResponse.closed?.name,
    modifiedDate: rawResponse.modified?.date,
    modifiedBy: rawResponse.modified?.name,
    workstreams: mapInboxEntryWorkstreams(rawResponse.workstreams),
    tasks: rawResponse.tasks,
    emailIdDetail: rawResponse['inbox-addresses'][0],
    subject: rawResponse.subject,
    html: rawResponse.html,
    inboxEntryId,
    isRead: rawResponse.read,
    isDeleted: rawResponse.trash === true,
    isMisc: rawResponse.misc,
    duplicate: rawResponse.duplicate,
    allowedStatusChanges: rawResponse['allowed-status-changes'],
    allowClose: geInboxEntryAllowClose(rawResponse['allow-close']),
    permissions: getEmailEntryPermissions(rawResponse.permissions),
    inboxEntry: rawResponse['inbox-entry'],
    shouldWaitSocketUpdate: rawResponse.shouldWaitSocketUpdate,
  };
};

export const useMappedEmailEntry = ({
  emailId,
  inboxEntryId,
  projectId,
}: useMappedEmailEntryProps) => {
  const emailEntryQuery = useEmailEntry({
    projectId,
    emailId,
    inboxEntryId,
  });

  const emailEntry = useMemo<EmailEntry | null>((): EmailEntry => {
    if (!emailEntryQuery.data) {
      return null;
    }

    const result = emailEntryQuery.data.data;

    if (!result) {
      return null;
    }

    return mapEmailEntry({ emailId, inboxEntryId, rawResponse: result });
  }, [emailEntryQuery.data, emailId, inboxEntryId]);

  return {
    emailEntry,
    isLoading: emailEntryQuery.isLoading,
    emailEntryQueryData: emailEntryQuery.data,
  };
};
